import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../common/Card';
import Input from '../common/Input';
import Button from '../common/Button';
import Alert from '../common/Alert';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

const StaffForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // always true initially to fetch roles
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    role_id: '',
    branch_id: '',
    is_active: 1
  });

  useEffect(() => {
    loadInitialData();
  }, [id]);

  const loadInitialData = async () => {
    try {
      // Fetch roles and branches
      const [rolesRes, branchesRes] = await Promise.all([
        api.get('/roles.php'),
        api.get('/branches.php')
      ]);
      setRoles(rolesRes.data.roles || []);
      setBranches(branchesRes.data.branches || []);
      
      if (isEdit) {
        await loadStaffMember();
      } else {
        setInitialLoading(false);
      }
    } catch (err) {
      console.error('Failed to load roles:', err);
      toast.error('Failed to load roles');
      setInitialLoading(false);
    }
  };

  const loadStaffMember = async () => {
    try {
      const response = await api.get(`/staff.php/${id}`);
      const data = response.data;
      setFormData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        username: data.username || '',
        email: data.email || '',
        phone: data.phone || '',
        password: '', // Password is not returned
        role_id: data.role_id || '',
        branch_id: data.branch_id || '',
        is_active: data.is_active
      });
    } catch (err) {
      console.error('Failed to load staff member:', err);
      toast.error('Staff member not found');
      navigate('/staff');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isEdit) {
        await api.put(`/staff.php/${id}`, formData);
        toast.success('Staff member updated successfully');
      } else {
        await api.post('/staff.php', formData);
        toast.success('Staff member created successfully');
      }
      navigate('/staff');
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === 'LIMIT_REACHED') {
        setError(data.message);
        toast.error('Subscription limit reached');
      } else {
        setError(data?.error || 'Failed to save staff member');
      }
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) return <div className="loading">Loading...</div>;

  return (
    <div className="staff-form-page" style={{ maxWidth: '600px', margin: '0 auto' }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
          <button 
            onClick={() => navigate('/staff')} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--text-light)' }}
          >
            ←
          </button>
          <h2 style={{ margin: 0 }}>{isEdit ? 'Edit Staff Member' : 'Add New Staff Member'}</h2>
        </div>

        {error && <Alert type="error" message={error} />}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <Input
              label="First Name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
            />
            <Input
              label="Last Name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
            />
          </div>

          <Input
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
            disabled={isEdit}
          />

          <Input
            label="Email Address"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={isEdit}
          />

          <Input
            label="Phone Number"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--slate-700)' }}>Role / Permissions</label>
            <select
              name="role_id"
              value={formData.role_id}
              onChange={handleChange}
              required
              style={{
                padding: '0.75rem',
                border: '1px solid var(--slate-300)',
                borderRadius: '8px',
                fontSize: '0.95rem'
              }}
            >
              <option value="">-- Select Role --</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.role_name} {role.is_default ? '(System)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Branch Assignment (Optional)</label>
            <select
              name="branch_id"
              value={formData.branch_id}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--slate-300)',
                borderRadius: '8px',
                fontSize: '0.95rem'
              }}
            >
              <option value="">-- All Branches / Headquarters --</option>
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </div>

          {!isEdit && (
            <Input
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="8"
            />
          )}

          {isEdit && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active === 1}
                onChange={handleChange}
              />
              <label htmlFor="is_active">Account Active</label>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
            <Button 
              variant="secondary" 
              type="button" 
              onClick={() => navigate('/staff')}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              loading={loading}
            >
              {isEdit ? 'Save Changes' : 'Create Staff Member'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default StaffForm;
